// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerStyles } from "@guestsupply/App/Components/Cart/CartLineContainer";
import LinesHeader, { LinesHeaderStyles } from "@guestsupply/App/Components/Cart/LinesHeader";
import Notify from "@guestsupply/App/Components/Notify";
import QtyField, { QtyFieldStyles } from "@guestsupply/App/Components/Qty/QtyField";
import xRemoveRmaProductImages from "@guestsupply/App/Handlers/RequestRma/RemoveRmaProductImages";
import xSetRmaProductImage from "@guestsupply/App/Handlers/RequestRma/SetRmaProductImage";
import FileUpload, { FileUploadPresentationProps } from "@guestsupply/MobiusOverrides/FileUpload/FileUpload";
import Tooltip, { TooltipPresentationProps } from "@guestsupply/MobiusOverrides/Tooltip/Tooltip";
import ProductAddToListLink from "@guestsupply/Overrides/Components/ProductAddToListLink";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@guestsupply/Overrides/Components/ProductPartNumbers";
import InfoAlert from "@guestsupply/XDL/Icons/InfoAlert";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import setCanSendReturnRequest from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetCanSendReturnRequest";
import setOrderLines from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetOrderLines";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel, WishListSettingsModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Icon, Typography } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { Limit } from "embla-carousel/embla-carousel-vanilla/limit";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    orderLines: state.pages.requestRma.orderLines,
    wishListSettings: getSettingsCollection(state).wishListSettings,
});

const mapDispatchToProps = {
    setOrderLines,
    setCanSendReturnRequest,
    addToWishList,
    setAddToListModalIsOpen,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RequestRmaProductListStyles {
    gridContainer?: GridContainerProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    returnReasonGridItem?: GridItemProps;
    qtyToReturnTextField?: TextFieldPresentationProps;
    returnReasonSelect?: SelectPresentationProps;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productBrandStyles?: ProductBrandStyles;
    productInfoPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoSubtotalGridItem?: GridItemProps;
    // XNG-Changes
    priceStyles?: SmallHeadingAndTextStyles;
    productTotalPriceHeadingAndText?: SmallHeadingAndTextStyles;
    actionsContainer?: InjectableCss;
    priceText?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    priceWrapper?: InjectableCss;
    priceHeading?: TypographyProps;
    // XNG-Changes: GSS-368
    lineHeader?: LinesHeaderStyles;
    cartLineContainer?: CartLineContainerStyles;
    returnReasonContainer?: InjectableCss;
    fileUploader?: FileUploadPresentationProps;
    addToListLink?: LinkPresentationProps;
    tooltipStyles?: TooltipPresentationProps;
    qtyLabel?: TypographyProps;
    qtyWrapper?: InjectableCss;
    qtyToReturnField?: QtyFieldStyles;
    fileUploaderNote?: TypographyPresentationProps;
    fileUploaderWrapper?: InjectableCss;
}

export const requestRmaProductListStyles: RequestRmaProductListStyles = {
    gridContainer: {
        css: css`
            margin-top: 35px;
            margin-bottom: 25px;
        `,
    },
    orderLineCardImageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
        css: css`
            padding-left: 0;
        `,
    },
    orderLineCardInfoGridItem: { width: [9, 9, 7, 7, 8] },
    orderLineCardInfoGridContainer: { gap: 10 },
    returnReasonGridItem: {
        width: [9, 9, 3, 3, 3],
        printWidth: 0,
        css: css`
            flex-direction: column;
            margin-left: auto !important;
        `,
    },
    qtyToReturnTextField: {
        cssOverrides: {
            formInputWrapper: css`
                width: 90px;
            `,
        },
    },
    productInfoGridItem: { width: [12, 12, 8, 8, 8] },
    productInfoGridContainer: { gap: 5 },
    productInfoBrandDescriptionGridItem: { width: 12 },
    productInfoBrandDescriptionGridContainer: { gap: 5 },
    productInfoBrandGridItem: { width: 12 },
    productInfoDescriptionGridItem: { width: 12 },
    productInfoPartNumbersGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbers: {
        erpNumberLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
        customerNameLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
        manufacturerItemLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
    },
    orderLineInfoGridItem: {
        width: [12, 12, 4, 4, 4],
        printWidth: 6,
    },
    orderLineInfoGridContainer: { gap: 10 },
    orderLinePriceGridItem: {
        width: [12, 12, 12, 12, 7],
        printWidth: 7,
    },
    productInfoQtyOrderedGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
    },
    productInfoSubtotalGridItem: { width: 12 },
};

// XNG-Changes: update structure
const ProductInfo = ({ orderLine }: { orderLine: OrderLineModel }) => {
    return (
        <>
            {/* {orderLine.brand && <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrandStyles} />} */}
            <ProductDescription product={orderLine} extendedStyles={styles.productDescriptionStyles} />
            <ProductPartNumbers
                productNumber={orderLine.productErpNumber}
                customerProductNumber={orderLine.customerProductNumber}
                manufacturerItem={orderLine.manufacturerItem}
                trackingNumber={orderLine.properties.trackingNumber}
                extendedStyles={styles.productPartNumbers}
            />
        </>
    );
};

// XNG Change: split qty, price and total in the same component
const OrderLineInfo = ({ orderLine, order }: { orderLine: OrderLineModel; order: OrderModel }) => {
    return {
        Price: (
            <StyledWrapper {...styles.priceWrapper}>
                <Typography {...styles.priceHeading}>{translate("Price")}</Typography>
                <Typography {...styles.priceText}>{orderLine.unitPriceDisplay}</Typography>
                <Typography {...styles.unitOfMeasure}>
                    {orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : ""}
                </Typography>
                <Tooltip
                    {...styles.tooltipStyles}
                    triggerComponent={<Icon src={InfoAlert} width={12} height={12} />}
                    text={siteMessage("Custom_Restocking_Fees")}
                />
            </StyledWrapper>
        ),
        Qty: <QtyField value={orderLine.qtyOrdered} editable={false} />,
        Total: (
            <SmallHeadingAndText
                {...styles.productTotalPriceHeadingAndText}
                heading={translate("Total")}
                text={orderLine.extendedUnitNetPriceDisplay}
            />
        ),
        Surcharges: (
            <SmallHeadingAndText
                {...styles.productTotalPriceHeadingAndText}
                heading={`${translate("Surcharges")}`}
                text={order?.properties?.totalFreightCharges as string}
            />
        ),
    };
};

const OrderLineCard = (props: {
    order: OrderModel;
    orderLine: OrderLineModel;
    totalQuantity: number;
    qtyChangeHandler: (event: React.ChangeEvent<HTMLInputElement>, lineNumber: number) => void;
    returnReasonChangeHandler: (
        event: React.ChangeEvent<HTMLSelectElement>,
        lineNumber: number,
        productErpNum: string,
        callback?: (param: string | null) => void,
    ) => void;
    wishListSettings?: WishListSettingsModel;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
}) => {
    const {
        order,
        orderLine,
        totalQuantity,
        qtyChangeHandler,
        returnReasonChangeHandler,
        wishListSettings,
        addToWishList,
        setAddToListModalIsOpen,
    } = props;
    const minQtyToReturn = totalQuantity > 0 ? 0 : 1;
    const qtyToReturnError =
        totalQuantity > 0 &&
        ((orderLine.rmaQtyRequested > orderLine.qtyOrdered &&
            siteMessage("Field_Max_Number", translate("QTY Returning"), orderLine.qtyOrdered.toString())) ||
            (orderLine.rmaQtyRequested < minQtyToReturn &&
                siteMessage("Field_Min_Number", translate("QTY Returning"), minQtyToReturn.toString())));

    // XNG-Changes
    const orderLineInfo = OrderLineInfo({
        orderLine,
        order,
    });
    const dispatch = useDispatch();

    const [file, setFile] = React.useState<any>(null);
    const [maxImageNumberErr, setMaxImageNumberErr] = React.useState<boolean>(false);
    const [incorrectFileExtension, setIncorrectFileExtension] = React.useState(false);
    const [isOverMaxFileSize, setIsOverMaxFileSize] = React.useState(false);
    const [count, setCount] = React.useState(0);
    const fileUploadRef = React.useRef({ value: "" } as HTMLInputElement);

    // XNG-Changes: GSS-368
    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        const overMaxFileSize = (uploadedFile?.size || 0) / 1048576 > 5;

        if (uploadedFile) {
            const fileExtension = getFileExtension(uploadedFile.name);
            const incorrectFileExtension = ["jpg", "jpeg", "png"].indexOf(fileExtension) === -1;
            setIncorrectFileExtension(incorrectFileExtension);
            setIsOverMaxFileSize(overMaxFileSize);
            if (!incorrectFileExtension && !overMaxFileSize) {
                // Read the selected file and convert it to a data URL
                const reader = new FileReader();
                reader.onload = e => {
                    const imageDataUrl = (e.target?.result as string).replace(/^.+,/, "");
                    // Dispatch an action to store the image data URL in store
                    dispatch(
                        xSetRmaProductImage({
                            productImage: imageDataUrl,
                            productErpNumber: orderLine.productErpNumber,
                            name: `IMAGE-${order.customerNumber}-${orderLine.productErpNumber}-${count}`,
                            onError: (status: boolean) => {
                                setMaxImageNumberErr(status);
                            },
                        } as any),
                    );
                    setCount(count + 1);
                };
                reader.readAsDataURL(uploadedFile);

                // Set the selected file for rendering
                setFile(uploadedFile);
            }
        }
    };
    const showSurcharges = order.properties.totalFreightCharges !== "$0.00";
    const fileUploadError = incorrectFileExtension
        ? translate("must be .jpg, .jpeg or .png")
        : undefined || isOverMaxFileSize
        ? siteMessage("guest_maxImageSize")
        : undefined || maxImageNumberErr
        ? siteMessage("guest_maxNumOfImage")
        : undefined;
    // XNG-Changes
    const toasterContext = React.useContext(ToasterContext);

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }
        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    return (
        <CartLineContainer
            data-test-selector={`rmaProductLine${orderLine.productId}`}
            extendedStyles={showSurcharges ? styles.cartLineContainer : {}}
        >
            <CartLineContainer.Image>
                {orderLine.productUri && orderLine.isActiveProduct && (
                    <Link href={orderLine.productUri}>
                        <LazyImage
                            {...styles.orderLineCardImage}
                            altText={orderLine.altText}
                            src={orderLine.mediumImagePath}
                        />
                    </Link>
                )}
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                <ProductInfo orderLine={orderLine} />
                {orderLine.canAddToWishlist && (
                    <Link onClick={addToListClickHandler} {...styles.addToListLink} aria-label="Add to List">
                        {translate("Add to List")}
                    </Link>
                )}
            </CartLineContainer.Description>
            <CartLineContainer.Price>{orderLineInfo.Price}</CartLineContainer.Price>
            <CartLineContainer.Qty>{orderLineInfo.Qty}</CartLineContainer.Qty>
            {order.properties.totalFreightCharges !== "$0.00" && (
                <CartLineContainer.Surcharges>{orderLineInfo.Surcharges}</CartLineContainer.Surcharges>
            )}
            <CartLineContainer.Total>{orderLineInfo.Total}</CartLineContainer.Total>
            <CartLineContainer.Notes>
                {/* XNG-Changes: fix actions vertical alignment and style on mobile */}
                <StyledWrapper {...styles.actionsContainer}>
                    <StyledWrapper {...styles.qtyWrapper}>
                        <Typography {...styles.qtyLabel}>{translate("QTY to Return")}</Typography>
                        <QtyField
                            extendedStyles={styles.qtyToReturnField}
                            label={translate("QTY to Return")}
                            showLabel={false}
                            type="qty"
                            value={orderLine.rmaQtyRequested.toString()}
                            min={minQtyToReturn}
                            max={orderLine.qtyOrdered}
                            error={qtyToReturnError}
                            data-test-selector={`requestRmaLine_qtyToReturn_${orderLine.id}`}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                qtyChangeHandler(event, orderLine.lineNumber)
                            }
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.returnReasonContainer}>
                        <Select
                            label={translate("Return Reason")}
                            {...styles.returnReasonSelect}
                            value={orderLine.returnReason || ""}
                            error={
                                orderLine.rmaQtyRequested > 0 &&
                                !orderLine.returnReason &&
                                siteMessage("Field_Required", translate("Return Reason"))
                            }
                            data-test-selector={`requestRmaLine_ReturnReason_${orderLine.id}`}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                                returnReasonChangeHandler(
                                    event,
                                    orderLine.lineNumber,
                                    orderLine.productErpNumber,
                                    setFile,
                                )
                            }
                        >
                            <option value="">{translate("Select a Reason Code")}</option>
                            {order.returnReasons?.map(returnReason => (
                                <option key={returnReason} value={returnReason}>
                                    {returnReason}
                                </option>
                            ))}
                        </Select>
                        {/* XNG-Changes: GSS-368 */}
                        {orderLine.returnReason?.toUpperCase() === "Product arrived damaged".toUpperCase() && (
                            <StyledWrapper {...styles.fileUploaderWrapper}>
                                <FileUpload
                                    {...styles.fileUploader}
                                    inputRef={fileUploadRef}
                                    fileName={file?.name || ""}
                                    accept=".jpg,jpeg,png"
                                    label={translate("Upload a File")}
                                    labelPosition="top"
                                    error={fileUploadError}
                                    onFileChange={fileChangeHandler}
                                    uid="rmaReturnReasonFileUpload"
                                    placeholder=".JPG .PNG"
                                />
                                <Typography {...styles.fileUploaderNote}>
                                    {translate("Max Allowed File Size of 5MB")}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </CartLineContainer.Notes>
        </CartLineContainer>
    );
};

const styles = requestRmaProductListStyles;

const RequestRmaProductList: FC<Props> = ({
    orderLines,
    setOrderLines,
    setCanSendReturnRequest,
    wishListSettings,
    addToWishList,
    setAddToListModalIsOpen,
}) => {
    const [totalQuantity, setTotalQuantity] = React.useState(0);
    const { value: order } = useContext(OrderStateContext);
    const dispatch = useDispatch();
    React.useEffect(() => {
        if (!order || !order.orderLines) {
            return;
        }

        setOrderLines({ orderLines: order.orderLines });
    }, [order?.orderLines]);

    React.useEffect(() => {
        setCanSendReturnRequest({
            value:
                orderLines.some(line => line.rmaQtyRequested > 0 && line.returnReason) &&
                !orderLines.some(
                    line => line.rmaQtyRequested > line.qtyOrdered || (line.rmaQtyRequested > 0 && !line.returnReason),
                ),
        });
    }, [orderLines]);

    if (!order || !order.orderLines) {
        return null;
    }

    const qtyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, lineNumber: number) => {
        let qty = parseInt(event.target.value, 10);
        if (event.target.value === "") {
            qty = 0;
        }
        const updatedOrderLines = orderLines.map(line =>
            line.lineNumber === lineNumber ? { ...line, rmaQtyRequested: qty } : line,
        );
        setOrderLines({ orderLines: updatedOrderLines });

        let totalQty = 0;
        updatedOrderLines.forEach(orderLine => {
            totalQty += orderLine.rmaQtyRequested > 0 ? 1 : 0;
        });

        setTotalQuantity(totalQty);
    };
    // XNG-Changes: GSS-368
    const returnReasonChangeHandler = (
        event: React.FormEvent<HTMLSelectElement>,
        lineNumber: number,
        productErpNum: string,
        callback?: (param: string | null) => void,
    ) => {
        setOrderLines({
            orderLines: orderLines.map(line =>
                line.lineNumber === lineNumber ? { ...line, returnReason: event.currentTarget.value } : line,
            ),
        });
        if (event.currentTarget.value?.toUpperCase() !== "Product arrived damaged".toUpperCase()) {
            // delete uploaded image for a product based on ERP number
            dispatch(
                xRemoveRmaProductImages({
                    productImageId: productErpNum,
                }),
            );
            // reset returnReason upload's file to null
            if (callback) {
                callback(null);
            }
        }
    };
    const showSurcharges = order.properties.totalFreightCharges !== "$0.00";
    return (
        <StyledWrapper {...styles.gridContainer} data-test-selector="requestRmaProductList">
            {orderLines?.length > 0 ? (
                <>
                    <LinesHeader
                        showSurcharges={showSurcharges}
                        extendedStyles={showSurcharges ? styles.lineHeader : {}}
                    >
                        <LinesHeader.Qty>{translate("QTY Ordered")}</LinesHeader.Qty>
                        <LinesHeader.Total>{translate("Subtotal")}</LinesHeader.Total>
                    </LinesHeader>
                    {orderLines.map(orderLine => (
                        <OrderLineCard
                            key={orderLine.id}
                            order={order}
                            orderLine={orderLine}
                            totalQuantity={totalQuantity}
                            qtyChangeHandler={qtyChangeHandler}
                            returnReasonChangeHandler={returnReasonChangeHandler}
                            wishListSettings={wishListSettings}
                            addToWishList={addToWishList}
                            setAddToListModalIsOpen={setAddToListModalIsOpen}
                        />
                    ))}
                    <AddToListModal />
                </>
            ) : (
                <Notify
                    {...{ fullWidth: true }}
                    type="danger"
                    data-test-selector="Custom_RequestRMA_NoOrderLinesAvailable"
                >
                    {siteMessage(
                        "Custom_RequestRMA_NoOrderLinesAvailable",
                        "There is currently no order lines available on this order.",
                    )}
                </Notify>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestRmaProductList),
    definition: {
        allowedContexts: ["RequestRmaPage"],
        group: "Return Request (RMA)",
        fieldDefinitions: [],
    },
};

export default widgetModule;
